/**
 * The Recipes screen's Signature and Enigma tags: screen 07's quadrants,
 * worked out from the same sales, prices and costs, so a drink is a Signature
 * on Recipes exactly when the report says it is.
 */

import { useEffect, useMemo, useState } from "react";
import { analyseMenu, gpTargetOf, servicePicks, type SalesRecord, type ServicePickItem } from "@baros/core";
import { useApp } from "../context.js";
import { hasFeature } from "../features.js";
import { menuApi } from "./api.js";

const NONE: ReadonlyMap<string, ServicePickItem> = new Map();

/**
 * The current venue's picks, by spec. Empty without a venue, without the
 * menu feature, before the sales load, or if they fail to: the library works
 * the same without them, so a failure costs the two tags and nothing else.
 */
export function useServicePicks(): ReadonlyMap<string, ServicePickItem> {
  const app = useApp();
  const { token, venueId } = app;
  const enabled = hasFeature(app.me, "menu") && venueId !== null;
  const [sales, setSales] = useState<readonly SalesRecord[]>([]);

  useEffect(() => {
    setSales([]);
    if (!enabled || !venueId) return;
    let live = true;
    menuApi
      .sales(token, venueId)
      .then((r) => {
        if (live) setSales(r.sales);
      })
      .catch(() => {
        // No tags rather than an error on a screen that is otherwise fine.
      });
    return () => {
      live = false;
    };
  }, [token, venueId, enabled]);

  const ingredientsById = useMemo(
    () => new Map(app.library.ingredients.map((i) => [i.ingredientId, i])),
    [app.library.ingredients],
  );

  return useMemo(() => {
    if (!enabled || sales.length === 0) return NONE;
    return servicePicks(
      analyseMenu({
        specs: app.library.specs,
        ingredientsById,
        overridesBySpecId: app.overrides,
        salesBySpecId: new Map(sales.map((s) => [s.specId, s])),
        gpTarget: gpTargetOf(app.venueState?.venue),
      }),
    );
  }, [enabled, sales, app.library.specs, ingredientsById, app.overrides, app.venueState]);
}
